import { useMemo, useRef, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Button, Text, TextField } from "@radix-ui/themes";

export interface SearchableSelectOption {
  value: string;
  label: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  width?: number;
  disabled?: boolean;
}

export default function SearchableSelect({ value, onChange, options, placeholder, searchPlaceholder, width = 320, disabled = false }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q));
  }, [options, search]);

  const displayLabel = options.find((o) => o.value === value)?.label || value || placeholder || "Select...";

  return (
    <Popover.Root open={disabled ? false : open} onOpenChange={(o) => { if (!disabled) { setOpen(o); if (o) setTimeout(() => inputRef.current?.focus(), 0); else setSearch(""); } }}>
      <Popover.Trigger asChild>
        <Button type="button" variant="surface" disabled={disabled} style={{ width: "100%", justifyContent: "space-between", fontWeight: "normal" }}>
          <Text size="2" truncate>{displayLabel}</Text>
          <Text size="1" color="gray">▾</Text>
        </Button>
      </Popover.Trigger>
      <Popover.Content
        align="start"
        sideOffset={4}
        style={{
          width,
          padding: 0,
          zIndex: 12000,
          backgroundColor: "var(--color-panel-solid)",
          border: "1px solid var(--gray-a5)",
          borderRadius: "var(--radius-3)",
          boxShadow: "var(--shadow-4)",
        }}
      >
        <div style={{ padding: 8, borderBottom: "1px solid var(--gray-5)" }}>
          <TextField.Root
            ref={inputRef}
            placeholder={searchPlaceholder || "Search..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="2"
          />
        </div>
        <div
          className="rt-ScrollAreaRoot"
          style={{ maxHeight: 250, overflowY: "auto", padding: 4 }}
          onWheel={(e) => { e.currentTarget.scrollTop += e.deltaY; e.stopPropagation(); }}
        >
          {filtered.length === 0 ? (
            <Text size="2" color="gray" style={{ display: "block", padding: 8 }}>No results found</Text>
          ) : filtered.map((opt) => (
            <div
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); setSearch(""); }}
              style={{
                padding: "6px 8px",
                borderRadius: 4,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: opt.value === value ? 600 : 400,
                backgroundColor: opt.value === value ? "var(--accent-3)" : undefined,
                color: "var(--gray-12)",
              }}
              onMouseEnter={(e) => { if (opt.value !== value) e.currentTarget.style.backgroundColor = "var(--gray-4)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = opt.value === value ? "var(--accent-3)" : ""; }}
            >
              {opt.label}
            </div>
          ))}
        </div>
        <style>{`
          .rt-ScrollAreaRoot::-webkit-scrollbar { width: 6px; }
          .rt-ScrollAreaRoot::-webkit-scrollbar-track { background: transparent; }
          .rt-ScrollAreaRoot::-webkit-scrollbar-thumb { background: var(--gray-6); border-radius: 3px; }
          .rt-ScrollAreaRoot::-webkit-scrollbar-thumb:hover { background: var(--gray-8); }
        `}</style>
      </Popover.Content>
    </Popover.Root>
  );
}
